import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { B2BCompany, Product } from 'npx-family-happy-common';
import { Observable, Subject, debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil } from 'rxjs';
import { B2BCompanyCatalogService } from '../b2b-company-catalog.service';
import { AREAS, CatalogFHService, PRODUCT_TYPES, errorMessage, lineOf, productTypeLabel } from '../catalog.model';

type Mode = 'create' | 'link';

/** Crea o modifica un prodotto del servizio, o ci sposta un prodotto esistente. */
@Component({
  selector: 'app-catalog-product-modal',
  templateUrl: './catalog-product-modal.component.html',
  styleUrls: ['./catalog-product-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
})
export class CatalogProductModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) company!: B2BCompany;
  @Input({ required: true }) service!: CatalogFHService;
  /** Valorizzato = modifica. */
  @Input() product: Product | null = null;
  /** Numero di prezzi del prodotto in modifica, per l'avviso prima di eliminarlo. */
  @Input() priceCount = 0;
  @Output() saved = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  readonly productTypes = PRODUCT_TYPES;
  readonly areas = AREAS;
  readonly lineOf = lineOf;
  readonly productTypeLabel = productTypeLabel;

  mode: Mode = 'create';
  saving = false;
  formInvalid = false;
  confirmDelete = false;
  errorText = '';

  form = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    type: new UntypedFormControl('SERVICE', [Validators.required]),
    stripeID: new UntypedFormControl(''),
  });

  search = new UntypedFormControl('');
  results: Product[] = [];
  searching = false;
  selected: Product | null = null;

  private unsubscribe = new Subject<void>();

  constructor(private catalog: B2BCompanyCatalogService) {}

  get isEdit() {
    return !!this.product;
  }

  get tone() {
    return this.areas[lineOf(this.service.businessLine).area].tone;
  }

  ngOnInit(): void {
    if (this.product) {
      this.form.patchValue({ name: this.product.name, type: this.product.type, stripeID: this.product.stripeID ?? '' });
    }
    this.search.valueChanges
      .pipe(
        startWith(''),
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((text: string) => {
          this.searching = true;
          return this.catalog.searchProducts(text.trim());
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: (products) => {
          this.results = products;
          this.searching = false;
        },
        error: () => (this.searching = false),
      });
  }

  setMode(mode: Mode) {
    this.mode = mode;
    this.errorText = '';
  }

  inThisService(product: Product) {
    return product.service?.id === this.service.id;
  }

  submit() {
    this.errorText = '';
    if (this.mode === 'link' && !this.isEdit) {
      if (!this.selected) {
        this.errorText = 'Seleziona il prodotto da collegare.';
        return;
      }
      this.run(this.catalog.saveProduct(this.selected, this.service, this.company), 'Prodotto collegato');
      return;
    }
    if (this.form.invalid) {
      this.formInvalid = true;
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value;
    const product: Partial<Product> = {
      ...(this.product ?? {}),
      name: value.name.trim(),
      type: value.type,
      stripeID: value.stripeID?.trim() ?? '',
    };
    this.run(this.catalog.saveProduct(product, this.service, this.company), this.isEdit ? 'Prodotto aggiornato' : 'Prodotto creato');
  }

  deleteProduct() {
    if (!this.product) return;
    if (!this.confirmDelete) {
      this.confirmDelete = true;
      return;
    }
    this.run(this.catalog.deleteProduct(this.product.id), 'Prodotto eliminato', 'Impossibile eliminare il prodotto.');
  }

  private run(request: Observable<unknown>, message: string, fallback = 'Impossibile salvare il prodotto.') {
    this.saving = true;
    request.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit(message);
      },
      error: (error) => {
        this.saving = false;
        this.confirmDelete = false;
        this.errorText = errorMessage(error, fallback);
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
